import { ServiceModules } from '@/lib/constants/services'
import { useAPI } from '@aw/lib'
import { AxiosError } from 'axios'
import { useCallback, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from './store'

import { clearUser, setError, setUser } from '../../store/slices/account'


export const useAccount = () => {
    const dispatch = useAppDispatch()

    const { user, isLoading, error, isAuthenticated } = useAppSelector((state) => state.account)

    const api = useAPI()

    // Fetch user using the existing API system
    const userQuery = api.get<User>('/user', {
        service: ServiceModules.AUTH,
        queryKey: ['user'],
        enabled: true,
        retry: 1,
    })



    // Sync API state with Redux state
    useEffect(() => {
        if (userQuery.data && !userQuery.error) {
            const userData = userQuery.data as unknown as User
            if (userData.type && userData.type !== 'ORG_INTERNAL_MEMBER') {
                const roleRedirects: Record<string, string> = {
                    APPLICANT: `${process.env.NEXT_PUBLIC_USER_URL}/dashboard`,
                    ORG_CLIENT: `${process.env.NEXT_PUBLIC_CLIENT_URL}/welcome`,
                }
                window.location.href = roleRedirects[userData.type] || `${process.env.NEXT_PUBLIC_APP_ORIGIN}/login`
                return
            }
            dispatch(setUser(userData))
        } else if (userQuery.error) {
            const status = (userQuery.error as AxiosError)?.response?.status
            const unauthPaths: (string | RegExp)[] = [
                '/login', '/register', '/signup', '/talent-search', '/forgot-password', '/reset-password',
                '/docs/developers/icons', '/docs/developers',
                /^\/invitations\/[^/]+\/accept$/,
                /^\/password-reset\/[^/]+$/,
            ]
            const isUnauthPath = unauthPaths.some((pattern) =>
                pattern instanceof RegExp
                    ? pattern.test(window.location.pathname)
                    : window.location.pathname === pattern
            )
            if (status === 401 && !isUnauthPath) {
                const absoluteRedirect = `${process.env.NEXT_PUBLIC_ORG_URL}${window.location.pathname}${window.location.search}`
                window.location.href = `${process.env.NEXT_PUBLIC_ORG_URL}/login?redirect=${encodeURIComponent(absoluteRedirect)}`
                return
            }
            dispatch(clearUser())
            dispatch(setError(userQuery.error.message || 'Failed to fetch user'))
        }
    }, [userQuery.data, userQuery.error, dispatch])

    // Methods
    const fetchUser = useCallback(async () => {
        try {
            await userQuery.refetch()
        } catch (error) {
            dispatch(setError(error instanceof Error ? error.message : 'Failed to fetch user'))
        }
    }, [userQuery, dispatch])

    const clearError = useCallback(() => {
        dispatch(setError(null))
    }, [dispatch])



    return {
        // State
        user,
        isLoading: isLoading || userQuery.isLoading,
        error: error || userQuery.error?.message,
        isAuthenticated,

        // Methods
        fetchUser,
        clearError,
        // Raw query data for backward compatibility
        refetch: userQuery.refetch,
        isRefetching: userQuery.isRefetching,
    }
}